import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Tag,
  Select,
  InputNumber,
  Space,
  Typography,
  message,
  Modal,
  Descriptions,
  Timeline,
  Statistic,
} from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { analyticsApi, UserBehavior } from '../services/api';
import { VIP_STATUS_LABELS, VIP_STATUS_COLORS, VipStatus } from '../types';
import dayjs from 'dayjs';

const { Option } = Select;
const { Title } = Typography;

interface UserBehaviorTableProps {
  selectedDate: string;
  loading: boolean;
}

const UserBehaviorTable: React.FC<UserBehaviorTableProps> = ({
  selectedDate,
  loading: parentLoading,
}) => {
  const [data, setData] = useState<UserBehavior[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [filters, setFilters] = useState({
    vipStatus: undefined as string | undefined,
    minDuration: undefined as number | undefined,
  });
  const [userDetailVisible, setUserDetailVisible] = useState<boolean>(false);
  const [userDetail, setUserDetail] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState<boolean>(false);

  // 加载用户行为数据
  const loadUserBehaviors = async () => {
    if (!selectedDate) return;

    try {
      setLoading(true);
      const response = await analyticsApi.getUserBehaviors(
        selectedDate,
        pagination.current,
        pagination.pageSize,
        filters.vipStatus,
        filters.minDuration
      );

      setData(response.users);
      setPagination(prev => ({
        ...prev,
        total: response.total_users,
      }));
    } catch (error) {
      message.error('加载用户行为数据失败');
      console.error('Failed to load user behaviors:', error);
    } finally {
      setLoading(false);
    }
  };

  // 监听依赖变化
  useEffect(() => {
    loadUserBehaviors();
  }, [selectedDate, pagination.current, pagination.pageSize, filters]);

  // 处理分页变化
  const handleTableChange = (newPagination: any) => {
    setPagination({
      ...pagination,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    });
  };

  // 处理筛选器变化
  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
    }));
    setPagination(prev => ({
      ...prev,
      current: 1, // 重置到第一页
    }));
  };

  // 查看用户详情
  const handleViewUserDetail = async (userId: string, userName: string) => {
    try {
      setDetailLoading(true);
      setUserDetailVisible(true);
      
      const response = await analyticsApi.getUserDetail(userId, selectedDate, selectedDate);
      setUserDetail(response);
    } catch (error) {
      message.error('加载用户详情失败');
      console.error('Failed to load user detail:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  // 格式化时长 - 统一使用分钟
  const formatDuration = (seconds: number) => {
    const minutes = Math.round(seconds / 60);
    return `${minutes}分`;
  };

  // 格式化时间
  const formatTime = (timeStr: string) => {
    return dayjs(timeStr).format('HH:mm:ss');
  };

  // 表格列定义
  const columns: ColumnsType<UserBehavior> = [
    {
      title: 'ユーザー情報',
      dataIndex: 'user_name',
      key: 'user_info',
      width: 240,
      align: 'left',
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 'bold', marginBottom: '6px', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={text === '退会済み' ? { color: '#ff4d4f' } : {}}>
              {text || `ユーザー${record.user_id.slice(-8)}`}
            </span>
            <Tag 
              color={VIP_STATUS_COLORS[record.vip_status as VipStatus] || 'default'}
              style={{ fontSize: '10px', padding: '1px 6px', lineHeight: '16px' }}
            >
              {VIP_STATUS_LABELS[record.vip_status as VipStatus] || 'FREE'}
            </Tag>
          </div>
          <div style={{ fontSize: '11px', color: '#666', lineHeight: '1.3' }}>
            {record.email && (
              <div style={{ marginBottom: '2px' }}>📧 {record.email}</div>
            )}
            {record.clinic && (
              <div>🏥 {record.clinic}</div>
            )}
          </div>
        </div>
      ),
    },
    {
      title: '地区',
      dataIndex: 'prefecture',
      key: 'prefecture',
      width: 100,
      align: 'center',
      render: (prefecture: string) => (
        <span style={{ fontSize: '13px' }}>
          {prefecture ? `📍 ${prefecture}` : '-'}
        </span>
      ),
    },
    {
      title: '専業',
      dataIndex: 'specialty',
      key: 'specialty',
      width: 140,
      align: 'center',
      render: (specialty: string) => (
        <span style={{ fontSize: '13px' }}>
          {specialty ? `👨‍⚕️ ${specialty}` : '-'}
        </span>
      ),
    },
    {
      title: 'セッション数',
      dataIndex: 'session_count',
      key: 'session_count',
      align: 'center',
      sorter: (a, b) => a.session_count - b.session_count,
    },
    {
      title: '総時間(分)',
      dataIndex: 'total_duration',
      key: 'total_duration',
      align: 'center',
      render: (duration: number) => {
        // API返回的是秒，需要转换为分钟
        const minutes = Math.round(duration / 60);
        return `${minutes}分`;
      },
      sorter: (a, b) => a.total_duration - b.total_duration,
    },
    {
      title: '活動回数',
      dataIndex: 'activity_count',
      key: 'activity_count',
      align: 'center',
      sorter: (a, b) => a.activity_count - b.activity_count,
    },
    {
      title: 'よく使うページ',
      dataIndex: 'pages_visited',
      key: 'pages_visited',
      align: 'center',
      render: (pages: Record<string, number>) => {
        if (!pages) return '-';
        
        // PAGE_TYPE_MAPPING - 只显示2到8的页面
        const validPageTypes = [
          '計測', 'ポリコン表', 'DIPモード', '重ね合わせ', 
          'プロフィログラム', '評価', 'PA analysis'
        ];
        
        // 过滤出有效的页面类型
        const filteredPages = Object.entries(pages)
          .filter(([pageName]) => validPageTypes.includes(pageName))
          .sort(([,a], [,b]) => b - a);
        
        const topPage = filteredPages[0];
        return topPage ? `${topPage[0]} (${topPage[1]}回)` : '-';
      },
    },
    {
      title: '最初/最後の活動',
      key: 'activity_times',
      align: 'center',
      render: (_, record) => (
        <div style={{ fontSize: '12px' }}>
          <div>最初: {record.first_activity ? formatTime(record.first_activity) : '-'}</div>
          <div>最後: {record.last_activity ? formatTime(record.last_activity) : '-'}</div>
        </div>
      ),
    },
  ];

  return (
    <>
      <Card
        title={
          <Space>
            <Title level={4} style={{ margin: 0 }}>活動詳細ログ</Title>
            <span style={{ color: '#999', fontSize: '14px' }}>Activity Detail Log</span>
          </Space>
        }
        extra={
          <Space>
            <Select
              placeholder="全ユーザー"
              style={{ width: 120 }}
              allowClear
              value={filters.vipStatus}
              onChange={(value) => handleFilterChange('vipStatus', value)}
            >
              <Option value="0">FREE</Option>
              <Option value="1">PRO</Option>
              <Option value="2">ACADEMIC</Option>
              <Option value="3">PARTNER</Option>
              <Option value="4">INTERNAL</Option>
            </Select>
            <InputNumber
              placeholder="最小時間(秒)"
              style={{ width: 120 }}
              min={0}
              value={filters.minDuration}
              onChange={(value) => handleFilterChange('minDuration', value)}
            />
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={data}
          rowKey="user_id"
          loading={loading || parentLoading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} を表示、全 ${total} 件`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1000 }}
          onRow={(record) => ({
            onClick: () => handleViewUserDetail(record.user_id, record.user_name),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>

      {/* 用户详情模态框 */}
      <Modal
        title="ユーザー詳細情報"
        open={userDetailVisible}
        onCancel={() => setUserDetailVisible(false)}
        footer={null}
        width={600}
        loading={detailLoading}
      >
        {userDetail && (
          <div>
            {/* 用户基本信息 */}
            <Descriptions bordered size="small" style={{ marginBottom: 20 }} column={2}>
              <Descriptions.Item label="ユーザー名" span={2}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <strong style={{ fontSize: '16px' }}>
                    {userDetail.user_info?.user_name || 'ユーザー名不明'}
                  </strong>
                  <Tag 
                    color={VIP_STATUS_COLORS[userDetail.user_info?.vip_status as VipStatus] || 'default'}
                    style={{ fontSize: '11px', padding: '2px 8px', lineHeight: '18px' }}
                  >
                    {VIP_STATUS_LABELS[userDetail.user_info?.vip_status as VipStatus] || 'FREE'}
                  </Tag>
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="メールアドレス" span={2}>
                {userDetail.user_info?.email ? (
                  <span style={{ fontSize: '14px' }}>
                    {userDetail.user_info.email}
                  </span>
                ) : (
                  '-'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="診療所" span={2}>
                {userDetail.user_info?.clinic_name ? (
                  <span 
                    style={{ 
                      color: '#1890ff', 
                      cursor: 'pointer',
                      textDecoration: 'underline'
                    }}
                    onClick={() => {
                      const searchQuery = userDetail.user_info.prefecture
                        ? `${userDetail.user_info.clinic_name} ${userDetail.user_info.prefecture}`
                        : userDetail.user_info.clinic_name;
                      const googleSearchUrl = `https://www.google.com/search?q=${encodeURIComponent(searchQuery)}`;
                      window.open(googleSearchUrl, '_blank');
                    }}
                  >
                    🏥 {userDetail.user_info.clinic_name}
                    {userDetail.user_info.prefecture && ` (${userDetail.user_info.prefecture})`}
                  </span>
                ) : (
                  '-'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="前回の使用時刻">
                {userDetail.user_info?.last_activity_in_db ? (() => {
                  const lastActivity = dayjs(userDetail.user_info.last_activity_in_db);
                  const currentDate = dayjs(selectedDate);
                  const daysDiff = currentDate.diff(lastActivity, 'day');
                  return `${daysDiff}日前`;
                })() : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="活動時間">
                {formatDuration(userDetail.stats?.total_duration || 0)}
              </Descriptions.Item>
              <Descriptions.Item label="セッション数">
                {userDetail.stats?.total_sessions || 0}
              </Descriptions.Item>
              <Descriptions.Item label="総操作数">
                {userDetail.stats?.total_behaviors || 0}
              </Descriptions.Item>
            </Descriptions>

            {/* ユーザーの詳細行動ログ */}
            <Title level={5} style={{ marginBottom: 16 }}>ユーザーの詳細行動ログ</Title>
            <div style={{ maxHeight: 400, overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: '6px', padding: '12px' }}>
              {userDetail.detailed_behaviors && userDetail.detailed_behaviors.length > 0 ? (
                <Timeline
                  items={userDetail.detailed_behaviors.map((behavior: any, index: number) => ({
                    key: index,
                    dot: <span style={{ fontSize: '16px' }}>{behavior.icon || '🔍'}</span>,
                    children: (
                      <div style={{ marginBottom: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                          <span style={{ fontWeight: 'bold', color: '#1890ff', fontSize: '13px' }}>
                            {dayjs(behavior.timestamp).format('MM-DD HH:mm:ss')}
                          </span>
                        </div>
                        <div style={{ marginBottom: '4px' }}>
                          <strong style={{ color: '#333', fontSize: '14px' }}>
                            {behavior.user_action}
                          </strong>
                        </div>
                        <div style={{ color: '#666', fontSize: '12px', marginBottom: '4px' }}>
                          {behavior.operation_intent}
                        </div>
                        {behavior.system_actions && behavior.system_actions.length > 0 && (
                          <div style={{ fontSize: '11px', color: '#999' }}>
                            システム動作: {behavior.system_actions.join(' → ')}
                          </div>
                        )}
                        {behavior.details && Object.keys(behavior.details).length > 0 && (
                          <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>
                            詳細: {JSON.stringify(behavior.details, null, 0)}
                          </div>
                        )}
                      </div>
                    ),
                  }))}
                />
              ) : (
                <div style={{ textAlign: 'center', color: '#999', padding: 32 }}>
                  この期間に詳細な行動データがありません
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default UserBehaviorTable;